import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, Pencil, Trash } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AccountManager } from '../types'
import useImpersonateB2BCustomer from '@/hooks/useImpersonateB2BCustomer'
import useImpersonateB2BPortal from '@/hooks/useImpersonateB2BPortal'
import { CustomerRole } from '@/types/customer'
import { useDemoMode } from '@/context/DemoModeContext'
import { features } from '@/config/features'

export const columns: ColumnDef<AccountManager, unknown>[] = [
  {
    id: 'name',
    accessorFn: row => `${row.name} ${row.surname}`,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => {
      const { isDemoMode } = useDemoMode()
      const managerName = `${row.original.name} ${row.original.surname}`

      return (
        <div className={`flex flex-col ${isDemoMode ? 'blur-sm select-none' : ''}`}>
          <span className="font-medium">{managerName}</span>
        </div>
      )
    },
  },
  {
    id: 'email',
    accessorFn: row => row.email,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    cell: ({ row }) => {
      const { isDemoMode } = useDemoMode()
      return (
        <div className={isDemoMode ? 'blur-sm select-none' : ''}>
          {row.getValue('email')}
        </div>
      )
    },
  },
  {
    id: 'b2b_company_name',
    accessorFn: row => row.b2b_company_name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Company" />
    ),
  },
  {
    id: 'role',
    accessorFn: row => row.role,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Role" />
    ),
    cell: ({ row }) => {
      const role = row.original.role
      if (!role) return <span className="text-muted-foreground">-</span>

      const roleDisplay = role
      const variant = role === CustomerRole.AGENT ? 'lightBlue' : 'green'

      return <Badge variant={variant}>{roleDisplay}</Badge>
    },
    enableSorting: true,
    meta: { width: '100px' },
  },
  {
    id: 'default_phone_number',
    accessorFn: row => row.default_phone_number,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Phone" />
    ),
    cell: ({ row }) => {
      const { isDemoMode } = useDemoMode()
      return (
        <div className={isDemoMode ? 'blur-sm select-none' : ''}>
          {row.getValue('default_phone_number')}
        </div>
      )
    },
    meta: { width: '130px' },
  },
  {
    id: 'managed_customers',
    accessorFn: row => row._count.managed_customers,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Managed Customers" />
    ),
    cell: ({ row }) => {
      const count = row.original._count.managed_customers
      return (
        <Badge variant="secondary">
          {count} customer{count !== 1 ? 's' : ''}
        </Badge>
      )
    },
    enableSorting: false,
    meta: { width: '150px' },
  },
  {
    id: 'impersonate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    cell: ({ row }) => {
      const { impersonate } = useImpersonateB2BCustomer()
      const { impersonatePortal } = useImpersonateB2BPortal()
      return (
        <div className="flex gap-1">
          {features.impersonation.storefront && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => impersonate(row.original.id)}
            >
              Impersonate
            </Button>
          )}
          {features.impersonation.b2bPortal && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => impersonatePortal(row.original.id)}
            >
              B2B Portal
            </Button>
          )}
        </div>
      )
    },
    enableSorting: false,
    enableHiding: true,
    meta: { width: '220px' },
  },
  // {
  //   id: "actions",
  //   cell: ({ row }) => {
  //     const accountManager = row.original;

  //     return (
  //       <DropdownMenu>
  //         <DropdownMenuTrigger asChild>
  //           <Button variant="ghost" className="h-8 w-8 p-0">
  //             <span className="sr-only">Open menu</span>
  //             <MoreHorizontal className="h-4 w-4" />
  //           </Button>
  //         </DropdownMenuTrigger>
  //         <DropdownMenuContent align="end">
  //           <DropdownMenuItem
  //             onClick={(e) => console.log(`Impersonate: ${accountManager.id}`)}
  //           >
  //             Impersnate
  //           </DropdownMenuItem>
  //           <DropdownMenuItem
  //             onClick={() => console.log(`Edit': ${accountManager.id}`)}
  //           >
  //             <Pencil className="mr-2 h-4 w-4" />
  //             Edit
  //           </DropdownMenuItem>
  //           <DropdownMenuItem
  //             className="text-red-600"
  //             onClick={(e) => {
  //               e.preventDefault();
  //               console.log(`Delete': ${accountManager.id}`);
  //             }}
  //           >
  //             <Trash className="mr-2 h-4 w-4" />
  //             Delete
  //           </DropdownMenuItem>
  //         </DropdownMenuContent>
  //       </DropdownMenu>
  //     );
  //   },
  // },
]
